import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import { GrooveSpinnerDirective } from '../spinner/groove-spinner.directive';

export type StateTone = 'loading' | 'empty' | 'error';
export type StateSize = 'sm' | 'md' | 'lg';

/**
 * `groove-state` — pattern tier.
 *
 * One component, three tones via the tone axis. Composes `.groove-spinner`
 * for tone="loading". Projection slots:
 *   - [stateIcon]   — leading icon (optional; hidden when tone="loading" spinner takes its place)
 *   - default       — message content
 *   - [stateAction] — trailing action (optional, e.g., retry button)
 *
 * Role/live-region:
 *   - tone="error"   → role="alert" (assertive, no aria-live needed)
 *   - tone="loading" → role="status" + aria-live="polite"
 *   - tone="empty"   → role="status" (no aria-live)
 */
@Component({
  selector: 'groove-state',
  standalone: true,
  imports: [GrooveSpinnerDirective],
  template: `
    <span class="groove-state-indicator">
      @if (tone() === 'loading') {
        <span grooveSpinner [attr.aria-hidden]="true"></span>
      }
      <ng-content select="[stateIcon]" />
    </span>
    <span class="groove-state-message">
      <ng-content />
    </span>
    <span class="groove-state-action">
      <ng-content select="[stateAction]" />
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'groove-state',
    '[attr.data-tone]': 'tone()',
    '[attr.data-size]': 'sizeAttr()',
    '[attr.role]': 'roleAttr()',
    '[attr.aria-live]': 'ariaLiveAttr()',
  },
})
export class GrooveStateComponent {
  readonly tone = input.required<StateTone>();
  readonly size = input<StateSize>('md');

  // Omit default attr to keep DOM clean.
  protected readonly sizeAttr = computed(() =>
    this.size() === 'md' ? null : this.size(),
  );

  protected readonly roleAttr = computed(() =>
    this.tone() === 'error' ? 'alert' : 'status',
  );

  // aria-live only when loading (polite); alert role handles its own assertive
  // announcement for error; empty needs no live region.
  protected readonly ariaLiveAttr = computed(() =>
    this.tone() === 'loading' ? 'polite' : null,
  );
}
